/**
 * Scroll maths for the pinned photo hero
 * (app/components/site/home/PhotoHero.tsx).
 *
 * `p` is how far the 150vh hero section has scrolled through: 0 with its top
 * at the top of the viewport, 1 when its bottom meets the bottom of the
 * viewport. The sticky stage is pinned in between, and every layer is a pure
 * function of `p`, so the component wires these into motion's `useTransform`
 * and nothing re-renders per frame. Lengths come back in viewport units (vh,
 * vw) so no layout is read while scrolling; the reference's `innerHeight`
 * terms are the same values written as vh.
 */

export function clamp01(x: number): number {
	return Math.max(0, Math.min(1, x))
}

/** The reference's `ez`: quadratic ease-in-out. */
export function easeInOutQuad(x: number): number {
	return x < 0.5 ? 2 * x * x : 1 - (-2 * x + 2) ** 2 / 2
}

/**
 * Eased 0..1 progress of `p` across the span `from`..`to` (the reference's
 * `seg`). 0 before the span, 1 after it.
 */
export function seg(p: number, from: number, to: number): number {
	if (to <= from) return p >= to ? 1 : 0
	return easeInOutQuad(clamp01((p - from) / (to - from)))
}

/** Three decimals is plenty for a transform and keeps the strings stable. */
function round(n: number): number {
	const r = Math.round(n * 1000) / 1000
	return r === 0 ? 0 : r
}

/* ---------- copy ---------- */

/** The headline block fades out over the first half of the pin. */
export function heroCopyOpacity(p: number): number {
	return round(1 - seg(p, 0.02, 0.5))
}

/** ...while lifting 70px. */
export function heroCopyTransform(p: number): string {
	return `translateY(${round(-seg(p, 0, 0.5) * 70)}px)`
}

/**
 * Once the copy is fully faded it must also leave the tab order and stop
 * taking clicks (the reference left an invisible "Get started" link over
 * the photo), so the layer goes `visibility: hidden` at the same point.
 */
export function heroCopyVisibility(p: number): 'visible' | 'hidden' {
	return heroCopyOpacity(p) > 0 ? 'visible' : 'hidden'
}

/* ---------- veil ---------- */

/** The white radial veil clears with the copy. */
export function heroVeilOpacity(p: number): number {
	return round(1 - seg(p, 0.02, 0.5))
}

/* ---------- photo ---------- */

/** The photo starts zoomed to 1.1 and settles to 1.02 while drifting up 8vh. */
export function heroPhotoTransform(p: number): string {
	const a = seg(p, 0, 1)
	return `translateY(${round(-a * 8)}vh) scale(${round(1.1 - 0.08 * a)})`
}

/* ---------- clouds ---------- */

export const HERO_CLOUD_COUNT = 12

/** Which way a cloud drifts as the pin plays: alternating by index. */
export type HeroCloudDrift = 'left' | 'right'

export interface HeroCloudSeed {
	/** Percent of the stage width. */
	left: number
	/** Percent of the stage height. */
	top: number
	/** vw */
	width: number
	/** vw */
	height: number
	drift: HeroCloudDrift
}

/**
 * The twelve blurred blobs that rise out of the bottom of the stage: a row
 * every 10% from just off the left edge, three heights, four widths.
 */
export function heroCloudSeeds(count = HERO_CLOUD_COUNT): HeroCloudSeed[] {
	return Array.from({ length: count }, (_, i) => ({
		left: -14 + i * 10,
		top: 82 + (i % 3) * 4,
		width: 38 + (i % 4) * 6,
		height: 16 + (i % 3) * 4,
		drift: i % 2 ? 'right' : 'left',
	}))
}

export const HERO_CLOUD_SEEDS: readonly HeroCloudSeed[] = heroCloudSeeds()

/**
 * Clouds start 30vh below their resting spot and rise into place while
 * drifting 3vw sideways, odd ones right, even ones left.
 */
export function heroCloudTransform(p: number, drift: HeroCloudDrift): string {
	const a = seg(p, 0, 1)
	const x = (drift === 'right' ? 1 : -1) * a * 3
	return `translate(${round(x)}vw, ${round(30 * (1 - a))}vh)`
}

/** Clouds appear through the middle of the pin. */
export function heroCloudOpacity(p: number): number {
	return round(seg(p, 0.2, 0.8))
}
